import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import { LoginCredentials } from '../types';
import ForgotPasswordModal from '../components/auth/ForgotPasswordModal';
import { useForm } from 'react-hook-form';
import {
  EyeIcon,
  EyeSlashIcon,
  EnvelopeIcon,
  LockClosedIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const Login: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [rememberSession, setRememberSession] = useState(true);
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();

  const { register, handleSubmit, formState: { errors } } = useForm<LoginCredentials>();

  const onSubmit = async (data: LoginCredentials) => {
    setIsLoading(true);
    setAuthError(null);
    try {
      const result = await login(data, { rememberSession });
      if (result.success) {
        toast.success('¡Bienvenido al sistema!');
      } else {
        const msg = result.error || 'Credenciales incorrectas';
        setAuthError(msg);
        toast.error(msg);
      }
    } catch {
      setAuthError('No se pudo conectar con el servidor. Intenta nuevamente.');
      toast.error('Error de conexión');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden flex items-center justify-center bg-[#05080f]">

      {/* ── Orbes animados ── */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {/* Orbe 1: grande indigo, arriba-izquierda */}
        <div className="login-orb-1 absolute -left-56 -top-56 h-[750px] w-[750px] rounded-full bg-indigo-600/20 blur-[140px]" />
        {/* Orbe 2: grande azul, abajo-derecha */}
        <div className="login-orb-2 absolute -bottom-48 -right-40 h-[650px] w-[650px] rounded-full bg-blue-500/20 blur-[130px]" />
        {/* Orbe 3: mediano violeta, centro-derecha */}
        <div className="login-orb-3 absolute right-1/4 top-1/4 h-[420px] w-[420px] rounded-full bg-violet-500/15 blur-[100px]" />
        {/* Orbe 4: pequeño cian, arriba-derecha */}
        <div className="login-orb-4 absolute right-12 top-12 h-[220px] w-[220px] rounded-full bg-cyan-400/10 blur-[70px]" />
        {/* Orbe 5: pequeño fúcsia, abajo-izquierda */}
        <div className="login-orb-5 absolute bottom-16 left-16 h-[180px] w-[180px] rounded-full bg-fuchsia-500/10 blur-[60px]" />
      </div>

      {/* ── Dot grid ── */}
      <div className="pointer-events-none absolute inset-0 bg-dot-grid opacity-[0.035]" />

      {/* ── Línea de acento superior ── */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-indigo-500/70 to-transparent login-border-glow" />

      {/* ── Contenido centrado ── */}
      <div className="login-card-enter relative z-10 w-full max-w-[420px] px-5 py-10 sm:px-0">

        {/* Logo + heading */}
        <div className="login-stagger-1 mb-8 flex flex-col items-center gap-3 text-center">
          {/* Badge con pulso */}
          <div className="login-logo-pulse relative flex h-[62px] w-[62px] items-center justify-center rounded-[18px] bg-gradient-to-br from-indigo-500 via-indigo-600 to-violet-600 shadow-xl shadow-indigo-900/60 ring-1 ring-white/15">
            <span className="text-[22px] font-black tracking-tight text-white">SF</span>
            {/* Mini anillo externo */}
            <span className="absolute -inset-1.5 rounded-[22px] border border-indigo-400/25" />
          </div>
          <div>
            <h1 className="text-[1.6rem] font-bold tracking-tight text-white">Sistema Funeraria</h1>
            <p className="mt-1 text-sm font-medium text-slate-400">Plataforma de gestión institucional</p>
          </div>
        </div>

        {/* Glass card */}
        <div className="relative overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.04] shadow-2xl shadow-black/60 backdrop-blur-2xl">
          {/* Shimmer top border */}
          <div className="h-px w-full bg-gradient-to-r from-transparent via-indigo-400/60 to-transparent login-border-glow" />

          {/* Brillo esquina superior izquierda */}
          <div className="pointer-events-none absolute -left-12 -top-12 h-32 w-32 rounded-full bg-indigo-500/10 blur-2xl" />

          <div className="px-7 py-8 sm:px-8 sm:py-9">

            {/* Header form */}
            <div className="login-stagger-2 mb-6">
              <h2 className="text-xl font-semibold text-white">Iniciar sesión</h2>
              <p className="mt-1 text-sm text-slate-500">Accede con tus credenciales institucionales</p>
            </div>

            {/* Error */}
            {authError && (
              <div className="login-stagger-1 mb-5 flex items-start gap-2.5 rounded-xl border border-rose-500/25 bg-rose-500/10 px-4 py-3">
                <ExclamationCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-rose-400" />
                <p className="text-sm text-rose-300">{authError}</p>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>

              {/* Email */}
              <div className="login-stagger-3">
                <label htmlFor="email" className="mb-2 block text-xs font-semibold uppercase tracking-widest text-slate-500">
                  Correo electrónico
                </label>
                <div className="relative group">
                  <EnvelopeIcon className="pointer-events-none absolute left-3.5 top-1/2 h-[17px] w-[17px] -translate-y-1/2 text-slate-600 transition-colors group-focus-within:text-indigo-400" />
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="correo@empresa.com"
                    disabled={isLoading}
                    {...register('email', {
                      required: 'El correo es requerido',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Correo inválido'
                      }
                    })}
                    className={`w-full rounded-xl border bg-white/[0.05] py-3 pl-10 pr-4 text-sm text-white placeholder-slate-600 outline-none transition-all duration-200 disabled:opacity-50
                      ${errors.email
                        ? 'border-rose-500/50 ring-2 ring-rose-500/15'
                        : 'border-white/10 hover:border-white/20 focus:border-indigo-500/50 focus:ring-2 focus:ring-indigo-500/15'
                      }`}
                  />
                </div>
                {errors.email && (
                  <p className="mt-1.5 flex items-center gap-1 text-xs text-rose-400">
                    <ExclamationCircleIcon className="h-3.5 w-3.5" />
                    {errors.email.message}
                  </p>
                )}
              </div>

              {/* Password */}
              <div className="login-stagger-4">
                <label htmlFor="password" className="mb-2 block text-xs font-semibold uppercase tracking-widest text-slate-500">
                  Contraseña
                </label>
                <div className="relative group">
                  <LockClosedIcon className="pointer-events-none absolute left-3.5 top-1/2 h-[17px] w-[17px] -translate-y-1/2 text-slate-600 transition-colors group-focus-within:text-indigo-400" />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    disabled={isLoading}
                    {...register('password', {
                      required: 'La contraseña es requerida',
                      minLength: { value: 6, message: 'Mínimo 6 caracteres' }
                    })}
                    className={`w-full rounded-xl border bg-white/[0.05] py-3 pl-10 pr-11 text-sm text-white placeholder-slate-600 outline-none transition-all duration-200 disabled:opacity-50
                      ${errors.password
                        ? 'border-rose-500/50 ring-2 ring-rose-500/15'
                        : 'border-white/10 hover:border-white/20 focus:border-indigo-500/50 focus:ring-2 focus:ring-indigo-500/15'
                      }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(p => !p)}
                    disabled={isLoading}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-600 transition-colors hover:text-slate-300 focus:outline-none disabled:opacity-40"
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showPassword
                      ? <EyeSlashIcon className="h-4 w-4" />
                      : <EyeIcon className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1.5 flex items-center gap-1 text-xs text-rose-400">
                    <ExclamationCircleIcon className="h-3.5 w-3.5" />
                    {errors.password.message}
                  </p>
                )}
              </div>

              {/* Recordar + olvidé */}
              <div className="login-stagger-5 flex items-center justify-between pt-0.5">
                <label className="group flex cursor-pointer items-center gap-2.5 select-none">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={rememberSession}
                      onChange={e => setRememberSession(e.target.checked)}
                      disabled={isLoading}
                      className="peer sr-only"
                    />
                    <div className="h-4.5 h-[18px] w-[18px] rounded-[5px] border border-white/20 bg-white/5 transition-all peer-checked:border-indigo-500 peer-checked:bg-indigo-600" />
                    {rememberSession && (
                      <CheckCircleIcon className="absolute inset-0 h-[18px] w-[18px] text-white" />
                    )}
                  </div>
                  <span className="text-sm text-slate-400 transition-colors group-hover:text-slate-300">
                    Recordar sesión
                  </span>
                </label>

                <button
                  type="button"
                  onClick={() => setIsForgotPasswordOpen(true)}
                  className="text-sm font-medium text-indigo-400 transition-colors hover:text-indigo-300 focus:outline-none"
                >
                  ¿Olvidaste la contraseña?
                </button>
              </div>

              {/* Submit button */}
              <div className="login-stagger-6 pt-1">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="login-btn-shimmer relative w-full overflow-hidden rounded-xl bg-gradient-to-r from-indigo-600 via-indigo-500 to-violet-600 px-6 py-3.5 text-sm font-semibold tracking-wide text-white shadow-lg shadow-indigo-950/60 transition-all duration-200 hover:brightness-110 hover:shadow-indigo-900/80 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:ring-offset-1 focus:ring-offset-transparent active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-55"
                >
                  {/* Brillo interno top */}
                  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />

                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3.5" className="opacity-25" />
                        <path fill="currentColor" className="opacity-80" d="M4 12a8 8 0 018-8V0C5.37 0 0 5.37 0 12h4z" />
                      </svg>
                      Verificando credenciales…
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Iniciar sesión
                    </span>
                  )}
                </button>
              </div>

            </form>
          </div>

          {/* Shimmer bottom border */}
          <div className="h-px w-full bg-gradient-to-r from-transparent via-white/5 to-transparent" />
        </div>

        <p className="login-stagger-6 mt-5 text-center text-sm text-slate-500">
          ¿Nueva funeraria?{' '}
          <Link to="/registro" className="font-medium text-indigo-400 hover:text-indigo-300">
            Registrarse y elegir plan
          </Link>
        </p>

        {/* Footer */}
        <p className="login-stagger-6 mt-4 text-center text-[11px] font-medium text-slate-700">
          © {new Date().getFullYear()} Sistema Funeraria · Todos los derechos reservados
        </p>
      </div>

      <ForgotPasswordModal
        isOpen={isForgotPasswordOpen}
        onClose={() => setIsForgotPasswordOpen(false)}
      />
    </div>
  );
};

export default Login;
